import React, {useEffect, useState} from "react";
import {
    BankAccountList,
    BankAccountRestControllerApiFactory,
    BankMovementList,
    BankMovementRestControllerApiFactory,
    BankStatementLineList,
    BankStatementRestControllerApiFactory,
    ReconciliationRestControllerApiFactory,
} from "arteco-api-client-ts";
import {fmtAmt} from "../../../../components/utils/numbers";
import {dateToString} from "../../../../components/utils/dates";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {AccountBalance, CheckCircleRounded, ManageSearchRounded, PostAddRounded, UploadFileRounded} from "@mui/icons-material";
import {useApiManager} from "../../../../components/utils/api";
import {btnSx, EmptyState, Page, PageHeader, Panel, SoftChip, tableContainerSx, tableSx} from "../../../../components/kit";
import {JournalCandidatesDialog, PostEntryDialog, SignedAmount} from "./journal_dialogs";

const selectableRowSx = (selected: boolean) => ({
    cursor: "pointer",
    "&.Mui-selected, &.Mui-selected:hover": {bgcolor: "rgba(41, 222, 186, 0.14)"},
    "& td:first-of-type": {boxShadow: selected ? "inset 3px 0 0 #29DEBA" : "none"},
});

export const BankStatementPage = () => {
    const apiManager = useApiManager();

    const [bankAccount, setBankAccount] = useState<BankAccountList | null>(null);
    const [bankAccountInput, setBankAccountInput] = useState("");
    const [bankAccountOptions, setBankAccountOptions] = useState<BankAccountList[]>([]);
    const [uploading, setUploading] = useState(false);

    const [statements, setStatements] = useState<BankStatementLineList[]>([]);
    const [movements, setMovements] = useState<BankMovementList[]>([]);
    const [selectedStatement, setSelectedStatement] = useState<number | null>(null);
    const [selectedMovement, setSelectedMovement] = useState<number | null>(null);
    const [autoCount, setAutoCount] = useState<number | null>(null);
    const [journalAutoCount, setJournalAutoCount] = useState<number | null>(null);
    const [searchLine, setSearchLine] = useState<BankStatementLineList | null>(null);
    const [postLine, setPostLine] = useState<BankStatementLineList | null>(null);

    const loadData = (accountId: number) => {
        const stmtCtrl = apiManager.factory(BankStatementRestControllerApiFactory);
        const movCtrl = apiManager.factory(BankMovementRestControllerApiFactory);
        // Pendientes y conciliadas: con el filtro de pendientes, «Ya conciliados» no salía nunca (#14706).
        apiManager.execute(stmtCtrl.listBankStatementLine(accountId))
            .then(list => setStatements(list as any));
        apiManager.execute(movCtrl.listBankMovement(undefined, undefined, undefined, undefined, undefined, "", false))
            .then(list => setMovements((list as any).filter((m: any) => !m.journalEntry)));
    };

    useEffect(() => {
        const ctrl = apiManager.factory(BankAccountRestControllerApiFactory);
        apiManager.execute(ctrl.listBankAccount(undefined, undefined, false))
            .then(list => setBankAccountOptions(list as any));
    }, []);

    useEffect(() => {
        if (bankAccount?.id) loadData(bankAccount.id);
    }, [bankAccount]);

    const handleReconcile = () => {
        if (!selectedStatement || !selectedMovement) return;
        const ctrl = apiManager.factory(ReconciliationRestControllerApiFactory);
        apiManager.execute(ctrl.reconcile(selectedStatement, selectedMovement), "Conciliado")
            .then(() => {
                setSelectedStatement(null);
                setSelectedMovement(null);
                if (bankAccount?.id) loadData(bankAccount.id);
            });
    };

    const handleUnreconcile = (line: BankStatementLineList) => {
        const done = () => { if (bankAccount?.id) loadData(bankAccount.id); };
        if (line.journalLineId) {
            const ctrl = apiManager.factory(BankStatementRestControllerApiFactory);
            apiManager.execute(ctrl.unreconcileFromJournalBankStatementLine(line.id!), "Desconciliado").then(done);
            return;
        }
        const ctrl = apiManager.factory(ReconciliationRestControllerApiFactory);
        apiManager.execute(ctrl.unreconcile(line.id!), "Desconciliado").then(done);
    };

    const handleJournalAutoReconcile = () => {
        if (!bankAccount?.id) return;
        const ctrl = apiManager.factory(BankStatementRestControllerApiFactory);
        apiManager.execute(ctrl.autoReconcileWithJournalBankStatementLine(bankAccount.id))
            .then(result => {
                setJournalAutoCount(result.reconciled ?? 0);
                loadData(bankAccount.id!);
            });
    };

    /** Tras conciliar o asentar desde un diálogo: se cierra, se quita la selección y se recarga. */
    const afterJournalAction = () => {
        setSearchLine(null);
        setPostLine(null);
        setSelectedStatement(null);
        if (bankAccount?.id) loadData(bankAccount.id);
    };

    const handleAutoReconcile = () => {
        if (!bankAccount?.id) return;
        const ctrl = apiManager.factory(ReconciliationRestControllerApiFactory);
        apiManager.execute(ctrl.autoReconcile(bankAccount.id))
            .then(result => {
                setAutoCount((result as any).length);
                loadData(bankAccount.id!);
            });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!bankAccount?.id || !e.target.files?.length) return;
        const file = e.target.files[0];
        setUploading(true);
        const stmtUploadCtrl = apiManager.factory(BankStatementRestControllerApiFactory);
        const fd = new FormData();
        fd.append('file', file);
        apiManager.execute(
            stmtUploadCtrl.uploadBankStatementLine(bankAccount.id!, fd as any, {headers: {'Content-Type': null as any}})
        ).then(lines => {
            apiManager.enqueueSnackbar(lines.length === 0
                ? "Ninguna línea nueva: el extracto ya estaba importado"
                : `${lines.length} líneas importadas`, {variant: "success"});
            loadData(bankAccount.id!);
        }).catch(() => {})
        .finally(() => {
            setUploading(false);
            e.target.value = "";
        });
    };

    const handleAccountSearch = (term: string) => {
        if (term.length < 1) return;
        apiManager.execute(apiManager.factory(BankStatementRestControllerApiFactory)
            .listBankStatementLine(undefined, undefined, undefined, undefined, term, false) as any)
            .catch(() => {});
        const ctrl = apiManager.factory(BankMovementRestControllerApiFactory);
        apiManager.execute(ctrl.listBankMovement(undefined, undefined, undefined, undefined, undefined, term, false) as any)
            .catch(() => {});
    };

    const unreconciled = statements.filter(s => !s.reconciled);
    const reconciled = statements.filter(s => s.reconciled);

    const selectedLine = unreconciled.find(l => l.id === selectedStatement);
    const selectedMov = movements.find(m => m.id === selectedMovement);

    return (
        <Page>
            <PageHeader title="Conciliación bancaria"
                        subtitle="Concilia cada línea del extracto con su apunte del diario (o con un movimiento sin asiento), o asiéntala si aún no está en la contabilidad. Lo que coincide en importe a ±3 días puede emparejarse solo."/>

            <Panel sx={{mb: 3}}>
                <Box sx={{display: "flex", alignItems: {md: "flex-start"}, gap: 2, flexDirection: {xs: "column", md: "row"}, flexWrap: {md: "wrap"}}}>
                    <Autocomplete
                        options={bankAccountOptions}
                        value={bankAccount}
                        inputValue={bankAccountInput}
                        onInputChange={(_, val) => setBankAccountInput(val)}
                        onChange={(_, val) => setBankAccount(val)}
                        getOptionLabel={(o) => `${o.name} — ${o.iban}`}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={(params) => (
                            <TextField {...params} label="Cuenta bancaria" size="small" fullWidth
                                       sx={{"& .MuiOutlinedInput-root": {borderRadius: 2}}}/>
                        )}
                        sx={{minWidth: {md: 320}, flexGrow: 1}}
                        noOptionsText="Sin cuentas bancarias"
                    />
                    {bankAccount && (
                        <Box>
                            <Button
                                variant="outlined"
                                component="label"
                                disabled={uploading}
                                startIcon={<UploadFileRounded/>}
                                sx={{...btnSx, whiteSpace: "nowrap"}}
                            >
                                {uploading ? "Importando..." : "Importar extracto"}
                                <input type="file" accept=".n43,.aeb,.q43,.txt,.csv,.dat" hidden onChange={handleFileUpload}/>
                            </Button>
                            <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                                Norma 43 descargada del banco (o CSV fecha;importe;descripcion;referencia)
                            </Typography>
                        </Box>
                    )}
                    {bankAccount && (
                        <Stack direction={{xs: "column", sm: "row"}} spacing={1} useFlexGap sx={{flexWrap: "wrap", flexShrink: 0}}>
                            <Button variant="contained" onClick={handleJournalAutoReconcile} sx={{...btnSx, whiteSpace: "nowrap"}}>
                                Conciliar con el diario (automático)
                            </Button>
                            <Button variant="outlined" onClick={handleAutoReconcile} sx={{...btnSx, whiteSpace: "nowrap"}}>
                                Con movimientos (±3 días)
                            </Button>
                        </Stack>
                    )}
                </Box>
                {journalAutoCount !== null && (
                    <Alert severity={journalAutoCount > 0 ? "success" : "info"} sx={{mt: 2, borderRadius: 2}}>
                        {journalAutoCount === 0
                            ? "Ninguna línea con una pareja única en el diario. Búscalas una a una."
                            : `${journalAutoCount} ${journalAutoCount === 1 ? "línea conciliada" : "líneas conciliadas"} con el diario (solo las de pareja única)`}
                    </Alert>
                )}
                {autoCount !== null && (
                    <Alert severity="success" sx={{mt: 2, borderRadius: 2}}>
                        {autoCount} conciliaciones automáticas con movimientos realizadas
                    </Alert>
                )}
            </Panel>

            {!bankAccount && (
                <Panel>
                    <EmptyState icon={<AccountBalance/>} title="Elige una cuenta bancaria"
                                description="Verás sus líneas de extracto pendientes y los movimientos que aún no tienen asiento."/>
                </Panel>
            )}

            {bankAccount && (
                <Stack spacing={3}>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr)"}, gap: 3}}>
                        <Panel title="Extracto bancario: pendientes" action={<SoftChip label={unreconciled.length} tone={unreconciled.length ? "warning" : "success"}/>}>
                            {unreconciled.length === 0 ? (
                                <EmptyState title="Ninguna línea pendiente."/>
                            ) : (
                                <TableContainer sx={tableContainerSx}>
                                    <Table size="small" sx={{...tableSx, tableLayout: "fixed"}}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{width: 100}}>Fecha valor</TableCell>
                                                <TableCell>Descripción</TableCell>
                                                <TableCell align="right" sx={{width: 110}}>Importe</TableCell>
                                                <TableCell sx={{width: 84}}/>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {unreconciled.map(line => (
                                                <TableRow
                                                    key={line.id}
                                                    hover
                                                    selected={selectedStatement === line.id}
                                                    onClick={() => setSelectedStatement(
                                                        selectedStatement === line.id ? null : line.id!
                                                    )}
                                                    sx={selectableRowSx(selectedStatement === line.id)}
                                                >
                                                    <TableCell sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(line.valueDate)}</TableCell>
                                                    <TableCell><Typography variant="body2" noWrap title={line.description}>{line.description ?? "-"}</Typography></TableCell>
                                                    <TableCell align="right"><SignedAmount amount={line.amount}/></TableCell>
                                                    <TableCell align="right" sx={{px: 0.5, whiteSpace: "nowrap"}} onClick={e => e.stopPropagation()}>
                                                        <Tooltip title="Buscar en el diario">
                                                            <IconButton size="small" aria-label="Buscar en el diario" onClick={() => setSearchLine(line)}>
                                                                <ManageSearchRounded fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Asentar">
                                                            <IconButton size="small" aria-label="Asentar" onClick={() => setPostLine(line)}>
                                                                <PostAddRounded fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Panel>

                        <Panel title="Movimientos bancarios sin asiento" action={<SoftChip label={movements.length} tone="neutral"/>}>
                            {movements.length === 0 ? (
                                <EmptyState title="Todos los movimientos tienen asiento."/>
                            ) : (
                                <TableContainer sx={tableContainerSx}>
                                    <Table size="small" sx={{...tableSx, tableLayout: "fixed"}}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{width: 100}}>Fecha</TableCell>
                                                <TableCell>Descripción</TableCell>
                                                <TableCell align="right" sx={{width: 110}}>Importe</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {movements.map(mov => (
                                                <TableRow
                                                    key={mov.id}
                                                    hover
                                                    selected={selectedMovement === mov.id}
                                                    onClick={() => setSelectedMovement(
                                                        selectedMovement === mov.id ? null : mov.id!
                                                    )}
                                                    sx={selectableRowSx(selectedMovement === mov.id)}
                                                >
                                                    <TableCell sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(mov.movementDate)}</TableCell>
                                                    <TableCell><Typography variant="body2" noWrap title={mov.description}>{mov.description ?? "-"}</Typography></TableCell>
                                                    <TableCell align="right"><SignedAmount amount={mov.amount}/></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Panel>
                    </Box>

                    {(selectedStatement || selectedMovement) && (
                        <Panel sx={{position: "sticky", bottom: 8, zIndex: 2}}>
                            <Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={{sm: "center"}} justifyContent="space-between">
                                <Box>
                                    <Typography variant="body2">
                                        Extracto: {selectedLine ? <><strong>{dateToString(selectedLine.valueDate)}</strong> · <SignedAmount amount={selectedLine.amount}/></> : <em>elige una línea</em>}
                                    </Typography>
                                    <Typography variant="body2">
                                        Movimiento: {selectedMov ? <><strong>{dateToString(selectedMov.movementDate)}</strong> · <SignedAmount amount={selectedMov.amount}/></> : <em>elige un movimiento</em>}
                                    </Typography>
                                </Box>
                                <Button variant="contained" color="success" size="large" startIcon={<CheckCircleRounded/>}
                                        disabled={!selectedStatement || !selectedMovement} onClick={handleReconcile} sx={btnSx}>
                                    Conciliar selección
                                </Button>
                            </Stack>
                        </Panel>
                    )}

                    {reconciled.length > 0 && (
                        <Panel title={`Ya conciliados (${reconciled.length})`}>
                            <TableContainer sx={tableContainerSx}>
                                <Table size="small" sx={{...tableSx, tableLayout: "fixed", minWidth: 640}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{width: 100}}>Fecha valor</TableCell>
                                            <TableCell>Descripción extracto</TableCell>
                                            <TableCell align="right" sx={{width: 110}}>Importe</TableCell>
                                            <TableCell>Conciliado con</TableCell>
                                            <TableCell sx={{width: 130}}/>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reconciled.map(line => (
                                            <TableRow key={line.id}>
                                                <TableCell sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(line.valueDate)}</TableCell>
                                                <TableCell>{line.description ?? "-"}</TableCell>
                                                <TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{fmtAmt(line.amount)}</TableCell>
                                                <TableCell>
                                                    {line.journalLineId ? (
                                                        <Typography variant="body2" sx={{wordBreak: "break-word"}}>
                                                            Asiento {line.journalEntryNumber ?? "sin número"} · {dateToString(line.journalEntryDate)}
                                                            <Typography component="span" variant="body2" color="text.secondary"> · {line.journalEntryDescription ?? ""}</Typography>
                                                        </Typography>
                                                    ) : (line.bankMovement?.description ?? "-")}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        color="warning"
                                                        sx={btnSx}
                                                        onClick={() => handleUnreconcile(line)}
                                                    >
                                                        Desconciliar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Panel>
                    )}
                </Stack>
            )}

            <JournalCandidatesDialog line={searchLine} onClose={() => setSearchLine(null)} onReconciled={afterJournalAction}
                                     onPost={l => { setSearchLine(null); setPostLine(l); }}/>
            <PostEntryDialog line={postLine} onClose={() => setPostLine(null)} onPosted={afterJournalAction}/>
        </Page>
    );
};
